// Панелът на рецепта в „Продадени рецепти" — същият мастър-детайл като
// складовата карта: хедър (рецепта, периодът, продадено · бруто · съставки ·
// последна продажба),
// табове По дни · Отчети · Съставки. На десктоп е вдясно от тесния списък
// (× затваря), под 1024 px — лист на цял екран с „← Продадени рецепти".
// Табът остава при смяна на рецептата с ↑/↓ — сравняваш няколко рецепти в
// един и същ изглед. Табовете: ←/→ между тях. Периодът е този на справката.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { CloseOutlined, LeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSoldDaily } from "../../../../hooks/useInventory";
import { useSoldRecipeBreakdown } from "../../../../hooks/useInventorySoldAnalysis";
import type { SoldRecipeReport } from "../../../../api/inventorySoldAnalysis";
import { fmtDateTime } from "../../components/dateTime";
import { formatMoney } from "../../components/numbers";
import { formatPeriod, periodToParams, type Period } from "../../components/period/periodPresets";
import { useUnitDecimals } from "../../components/qty";
import { formatCount } from "../components/soldReportModel";
import { useStockCardDocuments } from "../stockCard/useStockCardDocuments";
import { SoldRecipeDays } from "./SoldRecipeDays";
import { SoldRecipeIngredients } from "./SoldRecipeIngredients";
import { SoldRecipeReports } from "./SoldRecipeReports";
import { RECIPE_TABS, nextTab, type RecipeTab } from "./soldRecipeModel";
import type { SoldRow } from "./soldVarianceModel";

type Props = {
  row: SoldRow;
  period: Period;
  variant: "pane" | "sheet";
  onClose: () => void;
};

export const SoldRecipePanel = ({ row, period, variant, onClose }: Props) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<RecipeTab>("days");
  const params = useMemo(() => periodToParams(period), [period]);
  const breakdownQuery = useSoldRecipeBreakdown(row.sellableId, params);
  const breakdown = breakdownQuery.data;
  const dailyQuery = useSoldDaily("recipe", row.sellableId, params);
  const decimalsOf = useUnitDecimals();
  const documents = useStockCardDocuments();
  const tabRefs = useRef<Partial<Record<RecipeTab, HTMLButtonElement | null>>>({});
  const backRef = useRef<HTMLButtonElement | null>(null);

  // Листът на телефон/таблет покрива справката — фокусът влиза в него
  useEffect(() => {
    if (variant === "sheet") backRef.current?.focus();
  }, [variant]);

  const openPosting = (report: SoldRecipeReport) => {
    if (report.postingId == null) return;
    documents.openDocument({
      type: "report",
      id: report.postingId,
      date: report.postingDate ?? report.reportDate ?? period.to.toISOString(),
    });
  };

  const onTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const next = nextTab(tab, event.key === "ArrowRight" ? 1 : -1);
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  const tabLabels: Record<RecipeTab, { label: string; count: number | undefined }> = {
    days: {
      label: t("inventoryPage.sdTabDays"),
      count: dailyQuery.data?.filter((day) => day.quantity !== 0).length,
    },
    reports: { label: t("inventoryPage.sdTabReports"), count: breakdown?.reports.length },
    ingredients: { label: t("inventoryPage.sdTabIngredients"), count: breakdown?.ingredients.length },
  };

  return (
    <section
      data-sold-panel
      aria-label={t("inventoryPage.sdRecipePanelAria", { name: row.name })}
      className={`flex h-full min-h-0 flex-1 flex-col bg-panel ${variant === "pane" ? "border-l border-shade-2" : ""}`}
    >
      <header className="flex shrink-0 flex-col gap-2 border-b border-shade-2 px-4 pb-3 pt-3">
        {variant === "sheet" && (
          <button
            ref={backRef}
            type="button"
            onClick={onClose}
            className="flex cursor-pointer items-center gap-1.5 self-start rounded-md border-none bg-transparent p-0 text-[13px] leading-[normal] text-body outline-none hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--color-brand)]"
          >
            <LeftOutlined className="text-[11px]" />
            {t("inventoryPage.soldRecipes")}
          </button>
        )}
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[11px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted">
              {t("inventoryPage.sdRecipeKicker")} · {formatPeriod(period)}
            </div>
            <h2 className="m-0 mt-1 truncate text-lg font-bold leading-[1.2] tracking-[-0.01em] text-ink">{row.name}</h2>
          </div>
          {variant === "pane" && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t("inventoryPage.sdClosePanel")}
              title={`${t("inventoryPage.sdClosePanel")} (Esc)`}
              className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted outline-none hover:bg-shade-1 focus-visible:shadow-[0_0_0_2px_var(--color-brand)]"
            >
              <CloseOutlined className="text-sm" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] leading-[normal] text-body">
          <span>
            {t("inventoryPage.sdSoldLabel")}{" "}
            <span className="font-sheet text-[15px] font-bold tabular-nums text-brand-deep-ink">
              {formatCount(row.quantity)}
            </span>
          </span>
          <span>
            {t("inventoryPage.colGross")}{" "}
            <span className="font-sheet font-semibold tabular-nums text-ink">{formatMoney(row.totalSum)}</span>
          </span>
          <span>{t("inventoryPage.soldIngredientsLine", { count: row.ingredientsCount })}</span>
          {row.lastSoldAt && (
            <span>
              {t("inventoryPage.soldColLastSold")}{" "}
              <span className="font-sheet font-semibold tabular-nums text-ink">{fmtDateTime(row.lastSoldAt)}</span>
            </span>
          )}
        </div>
      </header>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-shade-2 px-4 py-2">
        <div
          role="tablist"
          aria-label={t("inventoryPage.sdRecipeKicker")}
          onKeyDown={onTabKeyDown}
          className="flex gap-0.5"
        >
          {RECIPE_TABS.map((key) => {
            const active = key === tab;
            return (
              <button
                key={key}
                ref={(element) => {
                  tabRefs.current[key] = element;
                }}
                type="button"
                role="tab"
                id={`sold-recipe-tab-${key}`}
                aria-selected={active}
                aria-controls="sold-recipe-tabpanel"
                tabIndex={active ? 0 : -1}
                onClick={() => setTab(key)}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border-none px-3 py-1.5 text-[13.5px] leading-[normal] outline-none focus-visible:shadow-[0_0_0_2px_var(--color-brand)] ${
                  active ? "bg-brand-tint font-semibold text-brand-deep-ink" : "bg-transparent text-body hover:bg-shade-1"
                }`}
              >
                {tabLabels[key].label}
                {tabLabels[key].count != null && (
                  <span className="font-sheet text-xs opacity-80">{tabLabels[key].count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div
        id="sold-recipe-tabpanel"
        role="tabpanel"
        aria-labelledby={`sold-recipe-tab-${tab}`}
        className="flex min-h-0 flex-1 flex-col"
      >
        {tab === "days" && <SoldRecipeDays daily={dailyQuery.data} isLoading={dailyQuery.isLoading} period={period} />}
        {tab === "reports" && (
          <SoldRecipeReports
            reports={breakdown?.reports}
            isLoading={breakdownQuery.isLoading}
            onOpenPosting={openPosting}
          />
        )}
        {tab === "ingredients" && (
          <SoldRecipeIngredients
            ingredients={breakdown?.ingredients}
            isLoading={breakdownQuery.isLoading}
            sold={breakdown?.quantity ?? row.quantity}
            decimalsOf={decimalsOf}
          />
        )}
      </div>
      {documents.modals}
    </section>
  );
};

export default SoldRecipePanel;
